/**
 * PlateDrawer Module
 * 
 * Contains drawing functions specific to plate visualization in cut-out configurator.
 * Handles plate background, dimensions display, and cut-out shapes.
 */

import { 
    calculateDistance, 
    calculateMidpoint, 
    calculatePerpendicularVector, 
    calculateCircleRadius,
    calculateCutoffPoints,
    calculateSagitta
} from '@/services/geometry.js';

/**
 * Draw a plate with dimensions and optional cut-out shapes
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {Object} canvasSize - Canvas dimensions {width, height}
 * @param {Object} plateDimensions - Plate dimensions {width, height} in mm
 * @param {Array} shapes - Array of shapes to draw on the plate
 * @param {string|null} selectedShapeId - ID of the currently selected shape
 * @param {string|null} hoveredShapeId - ID of the currently hovered shape
 * @param {Object} cutoffProperties - Cutoff configuration {type, x, y}
 * @param {boolean} showGuidelines - Show full guidelines (true) or sagitta-only (false)
 * @param {string|null} focusedRadiusField - Currently focused radius field name
 */
export const drawPlateWithShapes = (ctx, canvasSize, plateDimensions, shapes = [], selectedShapeId = null, hoveredShapeId = null, cutoffProperties = {}, showGuidelines = false, focusedRadiusField = null) => {
    // Scale factor to convert mm to canvas pixels
    const scale = Math.min(canvasSize.width / plateDimensions.width, canvasSize.height / plateDimensions.height) * 0.8;
    
    // Canvas center for plate positioning
    const centerX = canvasSize.width / 2;
    const centerY = canvasSize.height / 2;
    
    // Plate dimensions in canvas pixels
    const plateWidth = plateDimensions.width * scale;
    const plateHeight = plateDimensions.height * scale;
    
    // Plate position on canvas
    const plateX = centerX - plateWidth / 2;
    const plateY = centerY - plateHeight / 2;

    // Draw plate background with cutoff if applicable
    if (cutoffProperties.pos_tip_left > 0 && 
        (cutoffProperties.pos_right_bottom > 0 || cutoffProperties.pos_left_bottom > 0)) {
        drawPlateWithCutoff(ctx, plateX, plateY, plateWidth, plateHeight, plateDimensions, cutoffProperties, scale, showGuidelines, focusedRadiusField);
    } else {
        // Draw normal plate background
        ctx.fillStyle = '#6c757d';
        ctx.fillRect(plateX, plateY, plateWidth, plateHeight);
    }

    // Draw plate dimensions
    ctx.fillStyle = '#495057';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(
        `${plateDimensions.width}mm × ${plateDimensions.height}mm`,
        centerX,
        plateY - 25,
    );

    // Draw cut-out shapes
    drawCutOutShapes(ctx, shapes, plateX, plateY, plateWidth, plateHeight, plateDimensions, selectedShapeId, hoveredShapeId, showGuidelines);
};

/**
 * Draw plate with diagonal cutoff
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} plateX - Plate X position in canvas
 * @param {number} plateY - Plate Y position in canvas
 * @param {number} plateWidth - Plate width in canvas pixels
 * @param {number} plateHeight - Plate height in canvas pixels
 * @param {Object} plateDimensions - Plate dimensions in mm
 * @param {Object} cutoffProperties - Cutoff configuration
 * @param {number} scale - Scale factor from mm to pixels
 * @param {boolean} showGuidelines - Show full guidelines (true) or sagitta-only (false)
 * @param {string|null} focusedRadiusField - Currently focused radius field name
 */
const drawPlateWithCutoff = (ctx, plateX, plateY, plateWidth, plateHeight, plateDimensions, cutoffProperties, scale, showGuidelines, focusedRadiusField) => {
    // Calculate cutoff points and dimensions using geometry service
    const cutoffCalculation = calculateCutoffPoints(cutoffProperties, plateX, plateY, plateWidth, plateHeight, scale);
    
    // Destructure for easier access
    const {
        cutoffTopPx,
        cutoffRightPx,
        cutoffLeftBottomPx,
        curveRadiusPx,
        curveRadiusBottomPx,
        point1,
        point2,
        point3,
        hasLeftLine,
        hasRightLine
    } = cutoffCalculation;
    
    const point1X = point1.x;
    const point1Y = point1.y;

    const point2X = point2.x;
    const point2Y = point2.y;

    const point3X = point3.x;
    const point3Y = point3.y;
    
    // Draw the main plate shape
    ctx.fillStyle = '#6c757d';
    ctx.beginPath();
    
    // Start from bottom-left corner
    ctx.moveTo(plateX, plateY + plateHeight);
    
    // Handle left side based on whether left line exists
    if (hasLeftLine) {
        // Go to point 3 (left edge cutoff point)
        ctx.lineTo(point3X, point3Y);
        
        // Draw curved or straight line from point 3 to point 1 (tip)
        if (curveRadiusBottomPx > 0) {
            // Calculate chord length and sagitta from circle radius
            const leftLineLength = calculateDistance(point3X, point3Y, point1X, point1Y);
            const leftSagittaPx = calculateSagitta(curveRadiusBottomPx / scale, leftLineLength / scale) * scale;
            
            // Calculate midpoint of the line from point 3 to point 1
            const midpoint = calculateMidpoint(point3X, point3Y, point1X, point1Y);
            const midX = midpoint.x;
            const midY = midpoint.y;
            
            // Calculate perpendicular direction vector
            const perpVector = calculatePerpendicularVector(point3X, point3Y, point1X, point1Y);
            const perpX = perpVector.x;
            const perpY = perpVector.y;
            
            // Calculate control point for the curve using calculated sagitta
            const controlX = midX + perpX * leftSagittaPx * 2;
            const controlY = midY + perpY * leftSagittaPx * 2;
            
            // Draw quadratic curve
            ctx.quadraticCurveTo(controlX, controlY, point1X, point1Y);
        } else {
            // Draw straight line
            ctx.lineTo(point1X, point1Y);
        }
    } else {
        // Go to top-left corner
        ctx.lineTo(plateX, plateY);
        // Go to cutoff point 1 (top edge)
        ctx.lineTo(point1X, point1Y);
    }
    
    // Handle right side based on whether right line exists
    if (hasRightLine) {
        // Draw curved or straight line from point 1 to point 2 using main radius
        if (curveRadiusPx > 0) {
            // Calculate chord length and sagitta from circle radius
            const rightLineLength = calculateDistance(point1X, point1Y, point2X, point2Y);
            const rightSagittaPx = calculateSagitta(curveRadiusPx / scale, rightLineLength / scale) * scale;

            // Calculate midpoint of the straight line
            const midpoint = calculateMidpoint(point1X, point1Y, point2X, point2Y);
            const midX = midpoint.x;
            const midY = midpoint.y;
            
            // Calculate perpendicular direction vector (rotated 90° from line direction)
            const perpVector = calculatePerpendicularVector(point1X, point1Y, point2X, point2Y);
            const perpX = perpVector.x;  // Perpendicular X (rotate 90° clockwise)
            const perpY = perpVector.y; // Perpendicular Y
            
            // Calculate control point offset by sagitta amount
            // For quadratic Bézier, control point needs to be ~2x sagitta to achieve actual sagitta deviation
            const controlX = midX + perpX * rightSagittaPx * 2;
            const controlY = midY + perpY * rightSagittaPx * 2;
            
            // Draw quadratic curve using the offset midpoint as control point
            ctx.quadraticCurveTo(controlX, controlY, point2X, point2Y);

        } else {
            // Draw straight line when curvature is 0
            ctx.lineTo(point2X, point2Y);
        }
        
        // Go to bottom-right corner
        ctx.lineTo(plateX + plateWidth, plateY + plateHeight);
    } else {
        // No right line, go to top-right corner and then bottom-right
        ctx.lineTo(plateX + plateWidth, plateY);
        ctx.lineTo(plateX + plateWidth, plateY + plateHeight);
    }
    
    // Close the path back to bottom-left
    ctx.closePath();
    
    ctx.fill();
    
    // Draw curvature construction guides when radius > 0 and line exists
    if (hasRightLine && curveRadiusPx > 0) {
        drawCurvatureGuides(ctx, point1X, point1Y, point2X, point2Y, curveRadiusPx, scale, showGuidelines, focusedRadiusField, "curve_right");
    }
    
    // Draw curvature construction guides for bottom line when curve_left > 0 and left line exists
    if (hasLeftLine && curveRadiusBottomPx > 0) {
        drawCurvatureGuides(ctx, point3X, point3Y, point1X, point1Y, curveRadiusBottomPx, scale, showGuidelines, focusedRadiusField, "curve_left");
    }
    
    // Draw the cutoff lines for visual clarity
    ctx.strokeStyle = '#495057';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]); // Dashed line to show the cut
    
    // Main cutoff line from tip to right bottom (only if right line exists)
    if (hasRightLine) {
        ctx.beginPath();
        ctx.moveTo(point1X, point1Y);
        ctx.lineTo(point2X, point2Y);
        ctx.stroke();
    }
    
    // Additional cutoff line when left line exists
    if (hasLeftLine) {
        ctx.beginPath();
        ctx.moveTo(point3X, point3Y);
        ctx.lineTo(point1X, point1Y);
        ctx.stroke();
    }

    // Draw dots on the cutoff line coordinates
    ctx.fillStyle = '#dc3545';
    
    // Tip point (always shown when any line exists)
    ctx.beginPath();
    ctx.arc(point1X, point1Y, 3, 0, 2 * Math.PI);
    ctx.fill();

    // Right bottom point (only when right line exists)
    if (hasRightLine) {
        ctx.beginPath();
        ctx.arc(point2X, point2Y, 3, 0, 2 * Math.PI);
        ctx.fill();
    }
    
    // Left bottom point (only when left line exists)
    if (hasLeftLine) {
        ctx.beginPath();
        ctx.arc(point3X, point3Y, 3, 0, 2 * Math.PI);
        ctx.fill();
    }
    
    // Draw cutoff dimension labels
    ctx.fillStyle = '#dc3545'; // Red color for cutoff measurements
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Label for x (horizontal distance from left) - tip position
    const topLabelX = plateX + (cutoffTopPx / 2);
    const topLabelY = plateY - 10;
    ctx.fillText(`${cutoffProperties.pos_tip_left}mm`, topLabelX, topLabelY);
    
    // Label for y (vertical distance from bottom) - right bottom position (only when right line exists)
    if (hasRightLine) {
        const rightLabelX = plateX + plateWidth + 15;
        const rightLabelY = plateY + plateHeight - (cutoffRightPx / 2);
        ctx.save();
        ctx.translate(rightLabelX, rightLabelY);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(`${cutoffProperties.pos_right_bottom}mm`, 0, 0);
        ctx.restore();
    }
    
    // Label for left bottom position (only when left line exists)
    if (hasLeftLine) {
        const leftLabelX = plateX - 15;
        const leftLabelY = plateY + plateHeight - (cutoffLeftBottomPx / 2);
        ctx.save();
        ctx.translate(leftLabelX, leftLabelY);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(`${cutoffProperties.pos_left_bottom}mm`, 0, 0);
        ctx.restore();
    }
};

/**
 * Draw cut-out shapes on the plate
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context  
 * @param {Array} shapes - Array of shapes to draw
 * @param {number} plateX - Plate X position
 * @param {number} plateY - Plate Y position
 * @param {number} plateWidth - Plate width in pixels
 * @param {number} plateHeight - Plate height in pixels
 * @param {Object} plateDimensions - Plate dimensions {width, height} in mm
 * @param {string|null} selectedShapeId - ID of the currently selected shape
 * @param {string|null} hoveredShapeId - ID of the currently hovered shape
 * @param {boolean} showGuidelines - Show full guidelines (true) or sagitta-only (false)
 */
const drawCutOutShapes = (ctx, shapes, plateX, plateY, plateWidth, plateHeight, plateDimensions, selectedShapeId, hoveredShapeId, showGuidelines) => {
    shapes.forEach((shape) => {
        const isSelected = shape.identifier === selectedShapeId;
        const isHovered = shape.identifier === hoveredShapeId;
        
        switch (shape.shape) {

            case 'RECTANGLE': {
                // If explicit dimensions provided, use them; otherwise default square
                if (
                    shape.width && shape.height &&
                    (shape.width > 0) && (shape.height > 0)
                ) {
                    const scaleX = plateWidth / plateDimensions.width;
                    const scaleY = plateHeight / plateDimensions.height;

                    const rectW = shape.width * scaleX;
                    const rectH = shape.height * scaleY;

                    const rectX = plateX + shape.x * scaleX;
                    const rectY = plateY + shape.y * scaleY;

                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(rectX, rectY, rectW, rectH);

                    // Draw selection/hover border
                    if (isSelected || isHovered) {
                        ctx.strokeStyle = isSelected ? '#007bff' : '#FF8800';
                        ctx.lineWidth = isSelected ? 3 : 2;
                        ctx.strokeRect(rectX, rectY, rectW, rectH);
                    }

                    // Draw identifier text in center of rectangle
                    if (shape.identifier) {
                        ctx.fillStyle = '#000000';
                        ctx.font = '16px Arial';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        const centerTextX = rectX + rectW / 2;
                        const centerTextY = rectY + rectH / 2;
                        ctx.fillText(shape.identifier, centerTextX, centerTextY);
                    }
                }
                break;
            }
            case 'CIRCLE': {
                // If explicit radius and position provided, use them; otherwise default circle
                if (shape.radius && shape.radius > 0) {
                    const scaleX = plateWidth / plateDimensions.width;
                    const scaleY = plateHeight / plateDimensions.height;
                    const scale = Math.min(scaleX, scaleY); // Use uniform scaling for circles

                    const radius = shape.radius * scale;
                    const circleX = plateX + shape.x * scaleX;
                    const circleY = plateY + shape.y * scaleY;

                    ctx.fillStyle = '#ffffff';
                    ctx.beginPath();
                    ctx.arc(circleX, circleY, radius, 0, 2 * Math.PI);
                    ctx.fill();

                    // Draw selection/hover border
                    if (isSelected || isHovered) {
                        ctx.strokeStyle = isSelected ? '#007bff' : '#FF8800';
                        ctx.lineWidth = isSelected ? 3 : 2;
                        ctx.beginPath();
                        ctx.arc(circleX, circleY, radius, 0, 2 * Math.PI);
                        ctx.stroke();
                    }

                    // Draw identifier text in center of circle
                    if (shape.identifier) {
                        ctx.fillStyle = '#000000';
                        ctx.font = '16px Arial';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(shape.identifier, circleX, circleY);
                    }
                } 
                break;
            }
            default:
                break;
        }
    });
};

/**
 * Draw visual guides showing how the curvature radius is constructed
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} point1X - First cutoff point X coordinate
 * @param {number} point1Y - First cutoff point Y coordinate  
 * @param {number} point2X - Second cutoff point X coordinate
 * @param {number} point2Y - Second cutoff point Y coordinate
 * @param {number} curveRadiusPx - Circle radius in pixels
 * @param {number} scale - Scale factor from mm to pixels
 * @param {boolean} showGuidelines - Show full guidelines (true) or sagitta-only (false)
 * @param {string|null} focusedRadiusField - Currently focused radius field name
 * @param {string} radiusFieldName - Name of the radius field this guide represents
 */
const drawCurvatureGuides = (ctx, point1X, point1Y, point2X, point2Y, curveRadiusPx, scale, showGuidelines, focusedRadiusField, radiusFieldName) => {
    
    // Calculate the same geometry as used for the curve
    const midpoint = calculateMidpoint(point1X, point1Y, point2X, point2Y);
    const midX = midpoint.x;
    const midY = midpoint.y;
    const lineLength = calculateDistance(point1X, point1Y, point2X, point2Y);
    const perpVector = calculatePerpendicularVector(point1X, point1Y, point2X, point2Y);
    const perpX = perpVector.x;
    const perpY = perpVector.y;
    
    // Calculate sagitta from circle radius (input is now circle radius)
    const sagittaPx = calculateSagitta(curveRadiusPx / scale, lineLength / scale) * scale;
    
    // Control point for Bézier curve (2x sagitta for proper curve shape)
    const controlX = midX + perpX * sagittaPx * 2;
    const controlY = midY + perpY * sagittaPx * 2;
    
    // Actual sagitta point (true sagitta distance)
    const sagittaX = midX + perpX * sagittaPx;
    const sagittaY = midY + perpY * sagittaPx;
    
    // Save current context state
    ctx.save();
    
    // Always show sagitta guides (both false and true states show sagitta)
    // Draw perpendicular line from midpoint to actual sagitta point (showing true sagitta)
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2; // Thicker when focused
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(midX, midY);
    ctx.lineTo(sagittaX, sagittaY);
    ctx.stroke();
    
    // Draw guide points
    ctx.setLineDash([]); // Reset line dash
    ctx.fillStyle = '#000000'
    
    // Midpoint of straight line
    ctx.beginPath();
    ctx.arc(midX, midY, 3, 0, 2 * Math.PI); // Larger when focused
    ctx.fill();
    
    // Sagitta point
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(sagittaX, sagittaY, 3, 0, 2 * Math.PI); // Larger when focused
    ctx.fill();
    
    // Label for sagitta distance
    ctx.fillStyle = '#495057';
    ctx.font = '12px Arial'; // Bold when focused
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const labelX = (midX + sagittaX + 55) / 2;
    const labelY = (midY + sagittaY) / 2;
    ctx.fillStyle = '#000000';
    ctx.fillText(`${(sagittaPx / scale).toFixed(1)}mm`, labelX, labelY);

    // Draw the original straight line (what the curve would be without radius)
    ctx.strokeStyle = '#dc3545'; // Red color for guides
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]); // Small dashed line
    ctx.beginPath();
    ctx.moveTo(point1X, point1Y);
    ctx.lineTo(point2X, point2Y);
    ctx.stroke();

    // Check if this radius field is currently focused
    const isFocused = focusedRadiusField === radiusFieldName;
    
    // Only show full construction guides when showGuidelines is true (plaatopties tab)
    if (showGuidelines && isFocused) {
        
        // Use the input circle radius directly (it's now the leading value)
        const circleRadius = curveRadiusPx;
        const sagitta = sagittaPx;
        
        // Calculate the distance from midpoint to circle center
        const centerDistance = circleRadius - sagitta;
        
        // Circle center is along the perpendicular line, but in opposite direction from control point
        const circleCenterX = midX - perpX * centerDistance;
        const circleCenterY = midY - perpY * centerDistance;
        
        // Draw the circle center
        ctx.fillStyle = '#000000';
        ctx.beginPath();
        ctx.arc(circleCenterX, circleCenterY, 4, 0, 2 * Math.PI); // Larger when focused
        ctx.fill();
        
        // Draw a line from circle center to control point (showing the radius)
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.setLineDash([1, 2]);
        ctx.beginPath();
        ctx.moveTo(circleCenterX, circleCenterY);
        ctx.lineTo(controlX, controlY);
        ctx.stroke();
        
        // Draw lines from circle center to both endpoints (showing equal radii)
        ctx.beginPath();
        ctx.moveTo(circleCenterX, circleCenterY);
        ctx.lineTo(point1X, point1Y);
        ctx.moveTo(circleCenterX, circleCenterY);
        ctx.lineTo(point2X, point2Y);
        ctx.stroke();
        
        // Draw the full circle arc that our curve is part of (very light)
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.setLineDash([1, 4]); // Very light dashed line
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.globalAlpha = 1; // Reset opacity
        
        // Additional labels for full mode
        ctx.fillStyle = '#495057';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Label for circle center
        ctx.fillStyle = '#000000';
        ctx.fillText('○', circleCenterX - 12, circleCenterY - 8);
        
        // Label for circle radius
        const radiusLabelX = (circleCenterX + controlX) / 2;
        const radiusLabelY = (circleCenterY + controlY) / 2;
        ctx.font = '12px Arial'; // Bold when focused
        ctx.fillText(`R=${(curveRadiusPx / scale).toFixed(1)}mm`, radiusLabelX + 10, radiusLabelY);
    }
    
    // Restore context state
    ctx.restore();
}; 